import React from "react";
import { useDrop, useDrag } from "react-dnd";
import FormControlTextArea from "../../components/Common/FormControlTextArea";
import { Form } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import ExercisePlaceholder from "../../styles/Assets/exercise_placeholder_100_100.png";
import appInsights from "../../AppInsights";

const ExerciseItem = ({
  ex,
  position,       // array position after sort — used for drag/splice (NOT ex.indexOrder)
  session,
  isform,
  updateExerciseField,
  moveExercise,
  removeExercise,
}) => {
  const { trans } = useCustomTranslation();
  const ref = React.useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: "EXERCISE_ITEM",
    item: {
      position,   // pass sorted array position, not indexOrder
      sessionId: isform ? session.clientWorkoutPlanSessionId : session.id,
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: "EXERCISE_ITEM",
    hover: (item, monitor) => {
      if (!ref.current) return;

      const dragPosition = item.position;
      const hoverPosition = position;
      const dragSessionId = item.sessionId;
      const hoverSessionId = isform
        ? session.clientWorkoutPlanSessionId
        : session.id;

      // Don't replace items with themselves
      if (dragPosition === hoverPosition && dragSessionId === hoverSessionId) return;

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      if (dragPosition < hoverPosition && hoverClientY < hoverMiddleY) return;
      if (dragPosition > hoverPosition && hoverClientY > hoverMiddleY) return;

      // Time to actually perform the action
      if (dragSessionId === hoverSessionId) {
        moveExercise(dragPosition, hoverPosition);
        // Update the monitor item so hover keeps working smoothly
        item.position = hoverPosition;
        // Track exercise reordering
        appInsights.trackEvent({
          name: "Exercise_Reordered",
          properties: {
            sessionId: dragSessionId,
            fromIndex: dragPosition,
            toIndex: hoverPosition,
            exerciseId: ex.exerciseId,
            exerciseName: ex.name || ex.exerciseName,
            isForm: isform,
          },
        });
      }
    },
  });

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`exercise-card ${isDragging ? "dragging" : ""}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      {ex.blobUrl ? (
        <img src={ex.blobUrl} alt={ex.name} className="exercise-image" />
      ) : (
        <img src={ExercisePlaceholder} alt={ex.name} className="exercise-image" />
      )}
      <div className="exercise-details ml-2">
        <span className="exercise-name">
          {!isform
            ? ex.name?.charAt(0).toUpperCase() + ex.name?.slice(1)
            : ex.exerciseName?.charAt(0).toUpperCase() +
              ex.exerciseName?.slice(1)}
        </span>
        <div className="sets-reps">
          <div className="input-wrapper-session">
            <span className="input-label-session">
              {trans("ClientWorkout.sets")}
            </span>
            <input
              type="number"
              min="1"
              value={ex.sets}
              onChange={(e) =>
                updateExerciseField(ex.exerciseId, "sets", e.target.value)
              }
            />
          </div>

          <div className="input-wrapper-session">
            <span className="input-label-session">
              {trans("ClientWorkout.reps")}
            </span>
            <input
              type="number"
              min="1"
              max="100"
              value={ex.reps}
              onChange={(e) =>
                updateExerciseField(ex.exerciseId, "reps", e.target.value)
              }
            />
          </div>

          {/* {isform && ( */}
          <div className="input-wrapper-session">
            <span className="input-label-session">
              {trans("ClientWorkout.secs")}
            </span>
            <input
              type="number"
              min="1"
              max="100"
              value={ex.sec}
              onChange={(e) =>
                updateExerciseField(ex.exerciseId, "sec", e.target.value)
              }
            />
          </div>
          {/* )} */}
        </div>
        <div className="exercise-note">
          <Form.Group controlId={`note-${ex.exerciseId}`}>
            <FormControlTextArea
              placeholder="Add a note..."
              name="exerciseNote"
              value={ex.notes || ex.exerciseNote}
              onChange={(e) =>
                updateExerciseField(
                  ex.exerciseId,
                  "exerciseNote",
                  e.target.value,
                )
              }
              rows={2}
            />
          </Form.Group>
        </div>
      </div>

      <button
        className="remove-exercise"
        onClick={() => removeExercise(ex.exerciseId)}
      >
        X
      </button>
    </div>
  );
};

// Sorts exercises by indexOrder and reassigns indexOrder = array position.
// This normalizes data coming from the API (which returns unsorted exercises
// with non-contiguous indexOrder values) into a clean 0,1,2,3... sequence
// that matches array positions, so splice operations work correctly.
const getSortedAndNormalized = (exercises) =>
  [...exercises]
    .sort((a, b) => (a.indexOrder ?? 0) - (b.indexOrder ?? 0))
    .map((ex, idx) => ({ ...ex, indexOrder: idx }));


const Session = ({ session, setSessions, form, setForm, isform }) => {
  const { trans } = useCustomTranslation();
  const [{ isOver }, drop] = useDrop(() => ({
    accept: "EXERCISE",
    drop: (item) => {
      addExerciseToSession(item.exercise);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }));

  const addExerciseToSession = (exercise) => {
    // Track exercise added to session
    appInsights.trackEvent({
      name: "Exercise_Added_To_Session",
      properties: {
        exerciseId: exercise.exerciseId,
        exerciseName: exercise.name,
        sessionId: isform ? session.clientWorkoutPlanSessionId : session.id,
        sessionName: session.name || session.sessionName,
        isForm: isform,
      },
    });

    if (!isform) {
      setSessions((prevSessions) =>
        prevSessions?.map((s) => {
          if (s.id !== session.id) return s;
          if (s.exercises?.some((ex) => ex.exerciseId === exercise.exerciseId))
            return s;

          // Normalize existing before appending so indexOrders are contiguous
          const normalized = getSortedAndNormalized(s.exercises || []);
          return {
            ...s,
            exercises: [
              ...normalized,
              {
                ...exercise,
                sets: exercise.sets || "1",
                reps: exercise.reps || "1",
                sec: exercise.sec || "",
                exerciseNote: exercise.exerciseNote || "",
                // always next after normalized
                indexOrder: normalized.length, 
              },
            ],
          };
        })
      );
    } else {
      setForm((prevForm) => ({
        ...prevForm,
        clientWorkoutPlanSessionRequests:
          prevForm?.clientWorkoutPlanSessionRequests?.map((s) => {
            if (
              s.clientWorkoutPlanSessionId !==
              session.clientWorkoutPlanSessionId
            )
              return s;
            if (
              s?.clientWorkoutPlanExerciseRequests?.some(
                (ex) => ex.exerciseId === exercise.exerciseId
              )
            )
              return s;

            const normalized = getSortedAndNormalized(
              s.clientWorkoutPlanExerciseRequests || []
            );
              return {
                ...s,
                clientWorkoutPlanExerciseRequests: [
                  ...normalized,
                  {
                    clientWorkoutPlanSessionId:
                      session.clientWorkoutPlanSessionId,
                    clientWorkoutPlanExerciseId: uuidv4(),
                    exerciseName: exercise.name,
                    blobUrl: exercise.blobUrl || "",
                    exerciseId: exercise.exerciseId,
                    sets: "1",
                    reps: "1",
                    sec: "",
                    setType: "",
                    dropSet: "",
                    notes: exercise.note || "",
                    indexOrder: normalized.length,
                  },
                ],
              };
          }),
      }));
    }
  };

  // dragPosition and hoverPosition are ARRAY POSITIONS in the sorted+normalized
  // array — they match splice indices exactly.
  const moveExercise = (dragPosition, hoverPosition) => {
    if (isform) {
      setForm((prevForm) => {
        const sessionIndex =
          prevForm.clientWorkoutPlanSessionRequests.findIndex(
            (s) =>
              s.clientWorkoutPlanSessionId === session.clientWorkoutPlanSessionId
          );
        if (sessionIndex === -1) return prevForm;

        const updatedSessions = [...prevForm.clientWorkoutPlanSessionRequests];

        // Normalize first so array positions == indexOrder values
        const exercises = getSortedAndNormalized(
          updatedSessions[sessionIndex].clientWorkoutPlanExerciseRequests
        );

        const [removed] = exercises.splice(dragPosition, 1);
        exercises.splice(hoverPosition, 0, removed);

        // Reassign indexOrder to match new array positions
        updatedSessions[sessionIndex] = {
          ...updatedSessions[sessionIndex],
          clientWorkoutPlanExerciseRequests: exercises.map((ex, idx) => ({
            ...ex,
            indexOrder: idx,
          })),
        };

        return {
          ...prevForm,
          clientWorkoutPlanSessionRequests: updatedSessions,
        };
      });
    } else {
      setSessions((prevSessions) => {
        const sessionIndex = prevSessions.findIndex((s) => s.id === session.id);
        if (sessionIndex === -1) return prevSessions;

        const updatedSessions = [...prevSessions];
        // Normalize first so array positions == indexOrder values
        const exercises = getSortedAndNormalized(updatedSessions[sessionIndex].exercises);
        // Remove the dragged item
        const [removed] = exercises.splice(dragPosition, 1);
        exercises.splice(hoverPosition, 0, removed);

        // Reassign indexOrder to match new array positions
        updatedSessions[sessionIndex] = {
          ...updatedSessions[sessionIndex],
          exercises: exercises.map((ex, idx) => ({
            ...ex,
            indexOrder: idx,
          })),
        };

        return updatedSessions;
      });
    }
  };

  // Sort, remove by exerciseId, then reassign indexOrder from 0.
  // Sorting before filter ensures the remaining items keep their
  // relative visual order regardless of state insertion order.
  const removeExercise = (exerciseId) => {
    if (isform) {
      setForm((prevData) => ({
        ...prevData,
        clientWorkoutPlanSessionRequests:
          prevData.clientWorkoutPlanSessionRequests?.map((s) =>
            s.clientWorkoutPlanSessionId === session.clientWorkoutPlanSessionId
              ? {
                  ...s,
                  clientWorkoutPlanExerciseRequests: getSortedAndNormalized(
                    s.clientWorkoutPlanExerciseRequests
                  )
                    .filter((e) => e.exerciseId !== exerciseId)
                    .map((e, idx) => ({ ...e, indexOrder: idx })),
                }
              : s
          ),
      }));
    } else {
      setSessions((prevSessions) =>
        prevSessions.map((s) =>
          s.id === session.id
            ? {
                ...s,
                exercises: getSortedAndNormalized(s.exercises)
                  .filter((e) => e.exerciseId !== exerciseId)
                  .map((e, idx) => ({ ...e, indexOrder: idx })),
              }
            : s
        )
      );
    }
  };

  const updateExerciseField = (exerciseId, field, value) => {
    if (!isform) {
      setSessions((prevSessions) =>
        prevSessions?.map((s) =>
          s.id === session.id
            ? {
                ...s,
                exercises: s?.exercises?.map((exercise, index) =>
                  exercise.exerciseId === exerciseId
                    ? {
                        ...exercise,
                        [field]: value,
                        // Ensure indexOrder is always present
                        indexOrder: exercise.indexOrder ?? index,
                      }
                    : exercise,
                ),
              }
            : s,
        ),
      );
    } else {
      setForm((prevData) => ({
        ...prevData,
        clientWorkoutPlanSessionRequests:
          prevData?.clientWorkoutPlanSessionRequests?.map((plan) => ({
            ...plan,
            clientWorkoutPlanExerciseRequests:
              plan.clientWorkoutPlanExerciseRequests?.map((detail) =>
                detail.exerciseId === exerciseId
                  ? {
                      ...detail,
                      [field === "exerciseNote" ? "notes" : field]: value,
                    }
                  : detail
              ),
          })),
      }));
    }
  };

  // Sort and normalize for rendering — this is the source of truth for
  // what the user sees. `position` (array index) is passed to ExerciseItem,
  // not ex.indexOrder, so drag operations always use the correct splice index.
  const exercises = getSortedAndNormalized(
    isform
      ? session.clientWorkoutPlanExerciseRequests || []
      : session.exercises || []
  );

  return (
    <div ref={drop} className={`session ${isOver ? "highlight" : ""}  h-100`}>
      <h6>{!isform ? session.name : session.sessionName}</h6>
      {exercises.length === 0 ? (
        <p className="empty-text">{trans("ClientMealPlan.dragExerHere")}</p>
      ) : (
        exercises.map((ex, position) => (
          <ExerciseItem
            key={ex.exerciseId || ex.clientWorkoutPlanExerciseId || position}
            ex={ex}
            position={position}
            session={session}
            isform={isform}
            updateExerciseField={updateExerciseField}
            moveExercise={moveExercise}
            removeExercise={removeExercise}
          />
        ))
      )}
    </div>
  );
};

export default Session;